import React from "react";

interface FormInputProps {
  label: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  name?: string;
  max?: string | undefined;
  min?: string | undefined;
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  type,
  value,
  onChange,
  placeholder,
  name = "",
  max = undefined,
  min = undefined,
}) => {
  // Validate date when value exists
  const validateDate = () => {
    if (type !== "date" || !value) return null;

    const inputDate = new Date(value);

    if (min && inputDate < new Date(min.toString())) {
      return `Date must be on or after ${new Date(min.toString()).toLocaleDateString()}`;
    }

    if (max && inputDate > new Date(max.toString())) {
      return `Date must be on or before ${new Date(max.toString()).toLocaleDateString()}`;
    }

    return null;
  };

  const error = validateDate();

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {error && <span className="text-xs text-red-600 mt-1">{error}</span>}
      <input
        type={type}
        value={value}
        onChange={onChange} // Always allow onChange
        placeholder={placeholder}
        name={name}
        // Don't use min/max attributes for date inputs
        className={`px-3 py-2 text-sm border ${
          error ? "border-red-500" : "border-gray-300"
        } bg-[#F6F6F6] rounded focus:outline-none focus:ring-2 focus:ring-red-400`}
      />
    </div>
  );
};

interface FormTextAreaProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  name?: string;
}

export const FormTextArea: React.FC<FormTextAreaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  name = "",
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      name={name}
      className="px-3 py-2 text-sm border border-gray-300 bg-[#F6F6F6] rounded focus:outline-none focus:ring-2 focus:ring-red-400"
    />
  </div>
);

interface FormSelectProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  name?: string;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  name = "",
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value}
      name={name}
      onChange={onChange}
      className="px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-400"
      style={{ border: "1px solid #d1d5db" }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

interface FormCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  name?: string;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  label,
  checked,
  onChange,
  name = "",
}) => (
  <div className="flex items-center gap-2">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      name={name}
      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-400"
    />
    <label className="text-sm font-medium text-gray-700">{label}</label>
  </div>
);

export default FormInput;
